import { Component, DestroyRef, ElementRef, OnInit, Renderer2, signal } from '@angular/core';
import { switchMap, tap } from 'rxjs';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';
import { PageWithFiltersEnum } from 'src/app/models/filters/pageWithFilters.enum';
import { ISelectOption } from 'src/app/models/selectOptions';
import { FiltersService } from 'src/app/services/filters/filters.service';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { AiFindingsFiltersModel } from 'src/app/models/filters/implementations/pageFilters/aiFindingsFilters.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActiveFiltersHeightService } from 'src/app/services/infra/activeFiltersHeight/active-filters-height.service';
import { ToggleOption } from 'src/app/components/option-toggle/option-toggle.component';
import { ActivatedRoute, Router } from '@angular/router';
import {
  UnifiedPipelineService,
  SOURCE_QUERY_PARAM,
  SOURCE_QUERY_PARAM_TO_TOGGLE,
  DEFAULT_SOURCE_TOGGLE,
} from 'src/app/services/infra/unifiedPipeline/unified-pipeline.service';
import { SearchActivityHttpService } from 'src/app/services/httpServices/detection/searchActivityService/search-activity-http.service';

@Component({
  selector: 'app-enterprise-ai-findings',
  templateUrl: './enterprise-ai-findings.component.html',
  styleUrls: ['./enterprise-ai-findings.component.scss'],
})
export class EnterpriseAiFindingsComponent implements OnInit {
  // View toggle constants (Threads/Individuals)
  private static readonly THREADS_VIEW: ToggleOption = 'option1';
  private static readonly INDIVIDUALS_VIEW: ToggleOption = 'option2';

  protected readonly THREADS_VIEW = EnterpriseAiFindingsComponent.THREADS_VIEW;
  protected readonly INDIVIDUALS_VIEW = EnterpriseAiFindingsComponent.INDIVIDUALS_VIEW;

  public isFiltersMenuOpen = signal(false);

  public pageFilters = signal<AiFindingsFiltersModel | null>(null);
  public totalFindings = signal<number>(0);
  public totalThreads = signal<number>(0);
  public preventedCount = signal<number>(0);
  public hasPreventedCount = signal<boolean>(false);

  protected readonly isThreadsViewEnabled = this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.AiThreadsView);
  protected readonly isUnifiedPipelineEnabled = this._unifiedPipelineService.isEnabled();
  protected readonly viewToggle = signal<ToggleOption>(
    this.isThreadsViewEnabled ? this.THREADS_VIEW : this.INDIVIDUALS_VIEW
  );
  protected readonly sourceToggle = signal<ToggleOption>(DEFAULT_SOURCE_TOGGLE);

  private readonly _viewQueryParam = 'view';
  private readonly _optionToQueryParam: Record<ToggleOption, string> = {
    option1: 'threads',
    option2: 'individuals',
  };
  private readonly _queryParamToOption: Record<string, ToggleOption> = {
    threads: this.THREADS_VIEW,
    individuals: this.INDIVIDUALS_VIEW,
  };

  constructor(
    private readonly _filtersService: FiltersService,
    private readonly _activeFiltersHeightService: ActiveFiltersHeightService,
    private readonly _featureFlagsService: FeatureFlagsService,
    private readonly _renderer: Renderer2,
    private readonly _el: ElementRef,
    private readonly _destroyRef: DestroyRef,
    private readonly _router: Router,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _unifiedPipelineService: UnifiedPipelineService,
    private readonly _searchActivityHttpService: SearchActivityHttpService
  ) {
    this._activatedRoute.queryParams.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((params) => {
      if (this.isThreadsViewEnabled) {
        this.viewToggle.set(
          (this._queryParamToOption[params[this._viewQueryParam]] as ToggleOption) ?? this.THREADS_VIEW
        );
      }
      // Handle source toggle from URL using shared constants
      if (this.isUnifiedPipelineEnabled) {
        const sourceParam = params[SOURCE_QUERY_PARAM];
        this.sourceToggle.set(SOURCE_QUERY_PARAM_TO_TOGGLE[sourceParam] ?? DEFAULT_SOURCE_TOGGLE);
      }
    });
  }

  ngOnInit(): void {
    const filters = this._filtersService.getFilters(PageWithFiltersEnum.AiFindings) as AiFindingsFiltersModel;
    this.pageFilters.set(filters);

    // Initialize source toggle when UnifiedPipeline is enabled
    if (this.isUnifiedPipelineEnabled) {
      this._unifiedPipelineService.initializeSourceToggle();
      // Initialize the filter model with current toggle mode (don't clear selections on init)
      filters.setToggleMode(this.sourceToggle(), false);
    }

    this._activeFiltersHeightService.height$
      .pipe(
        tap((height: number) =>
          this._renderer.setStyle(this._el.nativeElement, '--applied-filters-height', `${height}px`, 1)
        ),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  public tablesFiltersChange(selectedOptions: ISelectOption[]) {
    const filters = this.pageFilters();
    if (filters) {
      filters.updateFilterFromUserSelection(undefined, selectedOptions);
    }
  }

  protected switchView($event: ToggleOption) {
    this.viewToggle.set($event);

    const viewQueryParam = { [this._viewQueryParam]: this._optionToQueryParam[$event] };

    this._router.navigate([], {
      queryParams: viewQueryParam,
      queryParamsHandling: 'merge',
    });
  }

  protected onBlockActionsCount(count: number) {
    this.preventedCount.set(count);
    this.hasPreventedCount.set(true);
  }

  protected switchSource($event: ToggleOption) {
    this.sourceToggle.set($event);
    this._unifiedPipelineService.updateSourceInUrl($event);

    // Update the filter model's toggle mode to switch AI Service filter options
    const filters = this.pageFilters();
    if (filters) {
      filters.setToggleMode($event);
    }
  }
}
